import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import type { ToastSeverity } from "../ui/toast";

dayjs.extend(utc);

// THE PLAYER PROFILE'S RULES, PORTED FROM `player-profile.component.ts` AND
// `player-profile-card.component.ts`. PURE, SO `profile-logic.test.ts` CAN PIN THEM.

// IN-PLAY INDICATOR -----------------------------------------------------------------

// `propertyInitials`: "Grand Casino of the West" -> "GCW"; "Main-Street Casino" -> "MSC"
export function propertyInitials(siteName: string): string {
  const skip = new Set(["of", "and", "the", "at", "on"]);
  const initials = siteName
    .split(/[\s-]+/)
    .filter((w) => w.length > 0 && !skip.has(w.toLowerCase()))
    .map((w) => w.replace(/[^a-zA-Z0-9]/g, "").charAt(0))
    .filter((l) => l.length > 0)
    .join("")
    .toUpperCase();
  return initials || siteName.toUpperCase();
}

// THE SERVER IGNORES `order_by=start_time`, SO THE NEWEST OPEN SESSION IS PICKED HERE; AN
// UNPARSEABLE START NEVER WINS
export function latestSession<T extends { startTime?: string }>(rows: T[] | null | undefined): T | null {
  let best: T | null = null;
  let bestAt = Number.NEGATIVE_INFINITY;
  for (const r of rows ?? []) {
    const t = new Date(r.startTime ?? "").getTime();
    const at = Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
    if (best === null || at > bestAt) {
      best = r;
      bestAt = at;
    }
  }
  return best;
}

// TRIPS ---------------------------------------------------------------------------

export const TRIP_FILTERS = [
  { name: "1 Day", value: "day" },
  { name: "1 Month", value: "month" },
  { name: "3 Months", value: "months-3" },
  { name: "6 Months", value: "months-6" },
  { name: "1 Year", value: "year" },
  { name: "Custom", value: "custom" },
] as const;
export type TripFilter = (typeof TRIP_FILTERS)[number]["value"];

export const TRIP_SORTS = [
  { name: "Day", value: "endTime" },
  { name: "Sessions", value: "sessionCount" },
  { name: "Duration", value: "duration" },
  { name: "Bets", value: "totalCoinIn" },
  { name: "Daily Actual", value: "totalDailyActual" },
] as const;
export type TripSort = (typeof TRIP_SORTS)[number]["value"];

export interface TimeFrame {
  start_time: Date;
  end_time: Date;
}

// `setCurrentTimeFrame`: FROM LOCAL MIDNIGHT N UNITS AGO TO THE END OF TODAY. A CUSTOM RANGE
// RUNS FROM THE START OF ITS FIRST DAY TO THE END OF ITS LAST; WITHOUT BOTH DATES IT RETURNS
// NULL, AND THE CALLER KEEPS THE FRAME IT HAD (ANGULAR'S BEHAVIOR TOO).
export function tripTimeFrame(filter: TripFilter, custom: { from: string; to: string } | null, now: Dayjs = dayjs()): TimeFrame | null {
  const end = now.endOf("day").toDate();
  switch (filter) {
    case "day":
      return { start_time: now.startOf("day").toDate(), end_time: end };
    case "month":
      return { start_time: now.subtract(1, "month").startOf("day").toDate(), end_time: end };
    case "months-3":
      return { start_time: now.subtract(3, "month").startOf("day").toDate(), end_time: end };
    case "months-6":
      return { start_time: now.subtract(6, "month").startOf("day").toDate(), end_time: end };
    case "year":
      return { start_time: now.subtract(1, "year").startOf("day").toDate(), end_time: end };
    case "custom": {
      if (!custom?.from || !custom.to) return null;
      const from = dayjs(custom.from);
      const to = dayjs(custom.to);
      if (!from.isValid() || !to.isValid() || to.isBefore(from, "day")) return null;
      return { start_time: from.startOf("day").toDate(), end_time: to.endOf("day").toDate() };
    }
  }
}

// `moment-date`: THE TRIP'S WALL-CLOCK DAY AS THE SERVER SENT IT - THE "Z" IS STRIPPED, SO
// THERE IS NO TIME-ZONE SHIFT
export function tripDate(startTime: string | undefined): string {
  if (!startTime) return "";
  return dayjs(startTime.replaceAll("Z", "")).format("MMM D, YYYY");
}

// `start-time`: `date:"M/d/yy HH:mm"` IN LOCAL TIME
export const startTimeText = (startTime: string | undefined) => (startTime ? dayjs(startTime).format("M/D/YY HH:mm") : "");

// `getTripDuration` ON THE TRIP PAGE: ONE DAY, OR "LAST START - FIRST START" (ANGULAR NAMES
// THEM THE OTHER WAY ROUND, BUT THIS IS THE ORDER IT PRINTS)
export function tripDurationText(sessions: Array<{ startTime?: string; endTime?: string }> | null | undefined): string {
  if (!sessions || sessions.length === 0) return "";
  const a = dayjs(sessions[0]!.startTime);
  const b = dayjs(sessions[sessions.length - 1]!.endTime);
  const fa = a.format("MMM D, YYYY");
  const fb = b.format("MMM D, YYYY");
  return fa === fb ? fa : `${fb} - ${fa}`;
}

// `data-view.searchItems` ON A TRIP'S SESSIONS: A CASE-INSENSITIVE MATCH ON MACHINE, LOCATION OR
// PROPERTY. ANGULAR APPENDS A ROW ONCE PER FIELD IT MATCHES (SO AN EMPTY SEARCH LISTS EVERY
// SESSION THREE TIMES); HERE EACH ROW APPEARS ONCE, IN ITS ORIGINAL ORDER.
export function filterTripSessions<T extends { description?: string; location?: string; siteName?: string }>(rows: T[], query: string): T[] {
  const q = query.toLocaleLowerCase();
  if (!q) return rows;
  return rows.filter((s) => [s.description, s.location, s.siteName].some((v) => v?.toString().toLocaleLowerCase().includes(q)));
}

// REINVESTMENT ---------------------------------------------------------------------

export function reinvestmentText(ratio: unknown): string {
  return typeof ratio === "number" && Number.isFinite(ratio) ? `${(ratio * 100).toFixed(2)}%` : "n/a";
}

// TASKS AND CONTACTS ---------------------------------------------------------------

// `RoundUpToNextQuarterHour`: ALWAYS ADVANCES, EVEN FROM A QUARTER HOUR EXACTLY
export function roundUpToNextQuarterHour(d: Dayjs): Dayjs {
  return d.add(15 - (d.minute() % 15), "minute").second(0).millisecond(0);
}

// THE NOTE A COMPLETED TASK OFFERS TO THE "CREATE CONTACT" FORM
export const taskContactNote = (t: { taskTitle?: string; outcomeName?: string; taskOutcomeNote?: string | null }) =>
  [t.taskTitle, t.outcomeName, t.taskOutcomeNote].filter(Boolean).join("\n");

export const completeAllContactNote = (notes: string[]) => notes.map((n, i) => `Task ${i + 1}: ${n}`).join("\n\n");

// CREATE-TASK SAVE CHECKS (THE PROFILE'S ARE AGAINST NOW, NOT THE START OF TODAY)
export function validateNewTask(start: Date, end: Date, now: Date = new Date()): string | null {
  if (start.getTime() < now.getTime()) return "The start time is in the past, please try again.";
  if (end.getTime() <= start.getTime()) return "The end time must be after the start time, please try again.";
  return null;
}

// EVENTS ---------------------------------------------------------------------------

export type TagSeverity = "info" | "warning" | "success" | "contrast" | "danger" | "secondary";

export function eventStatusSeverity(status: string | null | undefined): TagSeverity {
  switch (status) {
    case "BOOKING":
      return "info";
    case "CLOSED":
    case "NO-SHOW":
      return "warning";
    case "IN-PROGRESS":
      return "success";
    case "COMPLETED":
      return "contrast";
    case "CANCELLED":
    case "DECLINED":
      return "danger";
    default:
      return "secondary";
  }
}

// `FormatDate`: "Mon, Sep 28, 2026"
export const eventDate = (v: string | undefined) =>
  v ? new Date(v).toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" }) : "";

// CONFIRM / DECLINE ARE OFFERED FOR A LIVE BOOKING ONLY
export const canConfirmOrDecline = (e: { isBooked?: boolean; eventBookingStatus?: string | null }) =>
  !!e.isBooked && !["REDEEMED", "CANCELLED", "NO-SHOW"].includes(e.eventBookingStatus ?? "");

// PROFILE-ENTRY TOASTS (ISSUES #57, #66, #108, #113) ------------------------------------

export interface EntryToast {
  isInteractionFlag: boolean;
  priority: number;
  severity: ToastSeverity;
  title: string;
  message: string;
}

export interface QueuedNotification {
  id: number;
  severity?: string;
  title?: string;
  message?: string;
  priority?: number;
  isInteractionFlag?: boolean;
  oneTimeView?: boolean;
}

export interface GreetRuleMatch {
  name?: string;
  description?: string | null;
  priority?: number;
  playerType?: string;
}

// PRIMENG SEVERITIES AS THE SERVER SENDS THEM ("warn") TO THE TOAST'S
export function toastSeverity(s: string | undefined): ToastSeverity {
  if (s === "warn" || s === "warning") return "warning";
  if (s === "success" || s === "error" || s === "info") return s;
  return "info";
}

const rank = (p: unknown) => (typeof p === "number" && Number.isFinite(p) ? p : Number.POSITIVE_INFINITY);

// `mergedProfileEntryToasts`: QUEUED ITEMS AND PROBE MATCHES IN ONE RANKING - INTERACTION FLAGS
// FIRST ("DO NOT GREET" BEFORE ANY RULE SAYING GREET), THEN MANAGER PRIORITY ASCENDING, A
// MISSING PRIORITY LAST. THE SORT IS STABLE WITH THE QUEUED BLOCK FIRST, SO TIES KEEP THE
// SERVER'S ORDER. NO DEDUPING: THE TWO SOURCES SHARE NO RULE ID TO JOIN ON.
export function mergeEntryToasts(queued: QueuedNotification[], matches: GreetRuleMatch[], who: string): EntryToast[] {
  const q: EntryToast[] = queued.map((i) => ({
    isInteractionFlag: !!i.isInteractionFlag,
    priority: rank(i.priority),
    severity: toastSeverity(i.severity),
    title: i.title ?? "",
    message: i.message ?? "",
  }));
  const m: EntryToast[] = matches.map((g) => ({
    isInteractionFlag: false,
    priority: rank(g.priority),
    severity: "info",
    title: `Greet Rule: ${g.name ?? ""}`,
    message: g.description?.trim() || `${who} qualifies for this notification rule (${g.playerType}).`,
  }));
  return [...q, ...m].sort((a, b) => {
    if (a.isInteractionFlag !== b.isInteractionFlag) return a.isInteractionFlag ? -1 : 1;
    if (a.priority === b.priority) return 0;
    return a.priority < b.priority ? -1 : 1;
  });
}

// `buildProfileEntryPrompts`: THE BIRTHDAY (THE STORED MIDNIGHT-UTC DATE AGAINST LOCAL TODAY)
// AND MISSING-CONTACT NUDGES, WHICH ALWAYS SIT BELOW THE RANKED BLOCK
export function entryNudges(
  player: { birthdate?: string; firstName?: string; name?: string; mobile?: unknown; email?: unknown } | null,
  now: Dayjs = dayjs(),
): EntryToast[] {
  if (!player) return [];
  const out: EntryToast[] = [];
  const nudge = (severity: ToastSeverity, title: string, message: string) =>
    out.push({ isInteractionFlag: false, priority: Number.POSITIVE_INFINITY, severity, title, message });
  if (player.birthdate) {
    const b = dayjs.utc(player.birthdate);
    if (b.isValid() && b.month() === now.month() && b.date() === now.date()) {
      nudge("success", "Happy Birthday!", `Today is ${player.firstName || player.name || "This player"}'s birthday. Consider reaching out.`);
    }
  }
  const missing: string[] = [];
  if (!`${player.mobile ?? ""}`.trim()) missing.push("mobile number");
  if (!`${player.email ?? ""}`.trim()) missing.push("email address");
  if (missing.length > 0) {
    nudge("warning", "Missing Contact Info", `This player is missing a ${missing.join(" and ")}. Add it to keep their profile complete.`);
  }
  return out;
}

// THE DISMISSAL LEDGER (#113): A CLOSED TOAST IS COUNTED BY ITS CONTENT, AND A REBUILD SKIPS
// THAT MANY COPIES OF IT, SO A LATE REBUILD NEVER BRINGS BACK WHAT THE HOST ALREADY CLOSED
export const entryIdentity = (t: Pick<EntryToast, "severity" | "title" | "message">) => JSON.stringify([t.severity, t.title, t.message]);

export function keepUndismissed(toasts: EntryToast[], dismissed: ReadonlyMap<string, number>): EntryToast[] {
  const budget = new Map(dismissed);
  return toasts.filter((t) => {
    const id = entryIdentity(t);
    const left = budget.get(id) ?? 0;
    if (left === 0) return true;
    budget.set(id, left - 1);
    return false;
  });
}
